import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Box,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { createContentType } from "../CMSActions";
import ContentTypeFormStyles from "./ContentTypeFormStyles";
import { useAppDispatch } from "../../../store/hooks";

interface Props {
  open: boolean;
  onClose: () => void;
}

export interface IField {
  fieldLabel: string;
  fieldType: "text" | "multiline" | "number" | "image" | "link";
  required: boolean;
  maxLength?: number;
}

const ContentTypeForm: React.FC<Props> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const [contentTypeName, setContentTypeName] = useState("");
  const [fields, setFields] = useState<IField[]>([]);

  const handleAddField = () => {
    setFields([
      ...fields,
      {
        fieldLabel: "",
        fieldType: "text",
        required: false,
      },
    ]);
  };

  const handleFieldChange = (index: number, field: Partial<IField>) => {
    const updatedFields = [...(fields || [])];
    updatedFields[index] = { ...updatedFields[index], ...field };
    setFields(updatedFields);
  };

  const handleSubmit = () => {
    const createContentTypeRequestBody = {
      contentTypeName,
      fields,
    };
    dispatch(createContentType(createContentTypeRequestBody));
    onClose();
  };

  const handleDeleteField = (index: number) => {
    const updatedFields = fields?.filter((_, i) => i !== index) || [];
    setFields(updatedFields);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      id="content_type_form_dialog"
      data-test-id="content_type_form_dialog"
      aria-label="New Content Type Form"
    >
      <DialogTitle
        id="content_type_form_title"
        data-test-id="content_type_form_title"
        aria-label="New Content Type"
      >
        New Content Type
      </DialogTitle>
      <DialogContent>
        <Box 
          sx={ContentTypeFormStyles?.form}
          id="content_type_form_content"
          data-test-id="content_type_form_content"
          aria-label="Content Type Form Fields"
        >
          <TextField
            label="Content Type Name"
            value={contentTypeName}
            onChange={(e) => setContentTypeName(e.target.value)}
            fullWidth
            id="content_type_name_input"
            data-test-id="content_type_name_input"
            aria-label="Content Type Name Input"
          />

          {fields.map((field, index) => (
            <Box 
              key={index} 
              sx={ContentTypeFormStyles?.fieldRow}
              id={`content_type_field_row_${index}`}
              data-test-id={`content_type_field_row_${index}`}
              aria-label={`Field ${index + 1}`}
            >
              <TextField
                label="Field Label"
                value={field.fieldLabel}
                onChange={(e) =>
                  handleFieldChange(index, { fieldLabel: e.target.value })
                }
                id={`content_type_field_label_${index}`}
                data-test-id={`content_type_field_label_${index}`}
                aria-label={`Field Label ${index + 1}`}
              />
              <FormControl>
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={field.fieldType}
                  onChange={(e) =>
                    handleFieldChange(index, { 
                      fieldType: e.target.value as "text" | "multiline" | "number" | "image" | "link" 
                    })
                  }
                  id={`content_type_field_type_${index}`}
                  data-test-id={`content_type_field_type_${index}`}
                  aria-label={`Field Type ${index + 1}`}
                >
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="multiline">Multi-line Text</MenuItem>
                  <MenuItem value="number">Number</MenuItem>
                  <MenuItem value="image">Image</MenuItem>
                  <MenuItem value="link">Link</MenuItem>
                </Select>
              </FormControl>
              <TextField
                type="number"
                label="Max Length"
                value={field.maxLength || ""}
                onChange={(e) =>
                  handleFieldChange(index, {
                    maxLength: parseInt(e.target.value),
                  })
                }
                id={`content_type_field_max_length_${index}`}
                data-test-id={`content_type_field_max_length_${index}`}
                aria-label={`Field Max Length ${index + 1}`}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.required}
                    onChange={(e) =>
                      handleFieldChange(index, { required: e.target.checked })
                    }
                    id={`content_type_field_required_${index}`}
                    data-test-id={`content_type_field_required_${index}`}
                    aria-label={`Field Required ${index + 1}`}
                  />
                }
                label="Required"
              />
              <IconButton 
                onClick={() => handleDeleteField(index)}
                id={`content_type_field_delete_${index}`}
                data-test-id={`content_type_field_delete_${index}`}
                aria-label={`Delete Field ${index + 1}`}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}

          <Box>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddField}
              variant="outlined"
              sx={{ borderRadius: "4px" }}
              id="content_type_add_field_button"
              data-test-id="content_type_add_field_button"
              aria-label="Add Field"
            >
              Add Field
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose} 
          sx={{ borderRadius: "4px" }}
          id="content_type_cancel_button"
          data-test-id="content_type_cancel_button"
          aria-label="Cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          sx={{ borderRadius: "4px" }}
          id="content_type_save_button"
          data-test-id="content_type_save_button"
          aria-label="Save Content Type"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContentTypeForm;
